import {useCallback, useRef, useState} from 'react';
import {commitTicketingMutation} from '../../../graphql/forecastRelay';
import GenerateTicketReplyDraftMutation from '../../../../../mutations/ticketing/tickets/generateTicketReplyDraftMutation';

const NOT_CONFIGURED = 'AI drafting is not set up for this workspace yet. Ask an admin to enable it.';
const GENERIC = 'Could not draft a reply just now. Your message was left untouched — please try again.';

const ACCOUNT_DAILY = 'TICKET_AI_ACCOUNT_DAILY_CREDITS_EXCEEDED';
const ACCOUNT_MONTHLY = 'TICKET_AI_ACCOUNT_MONTHLY_CREDITS_EXCEEDED';

export interface DraftErrorExtensions {
	ticketingCode?: string;
	cap?: number;
	resetAt?: string;
}

// Formats a reset instant so both the account-scoped and per-ticket branches
// render the moment identically. Returns null for missing or unparseable input
// so callers never emit "Invalid Date" / "undefined" to the user.
function formatResetAt(value?: string): string | null {
	if (!value) return null;
	const parsed = new Date(value);
	if (isNaN(parsed.getTime())) return null;
	const time = parsed.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'});
	const date = parsed.toLocaleDateString([], {month: 'short', day: 'numeric'});
	return `at ${time} on ${date}`;
}

/**
 * Turns a mutation error into copy an agent can act on.
 *
 * Prefers the structured code the backend sends, because a per-ticket cap and an
 * account-wide spend ceiling are both 429 and need different copy — telling
 * someone they have used all their drafts "for this ticket" when the whole
 * workspace is out of AI credit sends them to another ticket to be refused again.
 * The message-parsing path is kept as a fallback for errors that carry no
 * extensions.
 */
export function friendlyDraftError(raw: string, extensions?: DraftErrorExtensions): string {
	const code = extensions?.ticketingCode;
	if (code === ACCOUNT_DAILY || code === ACCOUNT_MONTHLY) {
		const when = formatResetAt(extensions?.resetAt);
		const scope =
			code === ACCOUNT_MONTHLY
				? 'This workspace has used its AI allowance for this month.'
				: 'This workspace has used its AI allowance for today.';
		return when ? `${scope} AI drafting is available again ${when}.` : scope;
	}

	if (/not configured|not set up/i.test(raw)) return NOT_CONFIGURED;

	const capMatch = /\((\d+) per day\)/.exec(raw);
	if (/limit reached|rate limit/i.test(raw) || capMatch) {
		const cap = capMatch ? capMatch[1] : null;
		const resetMatch = /\d{4}-\d{2}-\d{2}T[\d:.]+Z/.exec(raw);
		const when = formatResetAt(resetMatch ? resetMatch[0] : undefined);
		const capPart = cap ? `You have used all ${cap} AI drafts for this ticket today.` : 'Daily AI draft limit reached.';
		return when ? `${capPart} You can draft again ${when}.` : capPart;
	}

	return GENERIC;
}

// Extract the ticketing extensions from whatever shape the Relay error carries.
// commitMutation surfaces the failure as an Error, but the underlying GraphQL
// error entry (with its extensions) can hang off a few different properties
// depending on the network layer: react-relay's own Error keeps them on
// `source.errors`, react-relay-network-modern parks the response on `res`, a
// bare GraphQLError exposes them at the top level, and an Apollo-style client
// uses `graphQLErrors`. Check each rather than assume one — a missed shape
// silently falls back to the message-parsing path with the wrong copy.
function extensionsFrom(err: unknown): DraftErrorExtensions | undefined {
	if (!err || typeof err !== 'object') return undefined;
	const e = err as {
		extensions?: unknown;
		source?: {errors?: Array<{extensions?: unknown}>};
		res?: {errors?: Array<{extensions?: unknown}>};
		graphQLErrors?: Array<{extensions?: unknown}>;
	};
	const raw =
		e.extensions ?? e.source?.errors?.[0]?.extensions ?? e.res?.errors?.[0]?.extensions ?? e.graphQLErrors?.[0]?.extensions;
	if (!raw || typeof raw !== 'object') return undefined;
	const {ticketingCode, cap, resetAt} = raw as DraftErrorExtensions;
	if (ticketingCode === undefined && cap === undefined && resetAt === undefined) return undefined;
	return {ticketingCode, cap, resetAt};
}

interface Options {
	onCompleted?: (draftText: string) => void;
	onError?: (error: Error) => void;
}

/**
 * Requests an AI-drafted reply for a ticket.
 *
 * `generate` resolves with the draft text and rejects on failure, so the caller can
 * `await` it and only touch the editor on success. `error` carries display-ready
 * copy for the failure cases (over cap, AI not configured, generation failed).
 */
export function useGenerateReplyDraft(options?: Options) {
	const [inFlight, setInFlight] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// Keep the latest callbacks out of `generate`'s identity so wiring it to an
	// onClick does not re-create the handler on every parent render.
	const optionsRef = useRef(options);
	optionsRef.current = options;

	const reset = useCallback(() => setError(null), []);

	const generate = useCallback((ticketId: string): Promise<string> => {
		setInFlight(true);
		setError(null);
		return new Promise<string>((resolve, reject) => {
			commitTicketingMutation(
				GenerateTicketReplyDraftMutation,
				{ticketId},
				(response: unknown) => {
					const draftText = (response as {generateTicketReplyDraft?: {draftText?: string}} | null)
						?.generateTicketReplyDraft?.draftText;
					setInFlight(false);
					if (!draftText) {
						const err = new Error(GENERIC);
						setError(GENERIC);
						optionsRef.current?.onError?.(err);
						reject(err);
						return;
					}
					optionsRef.current?.onCompleted?.(draftText);
					resolve(draftText);
				},
				(rawError: unknown) => {
					const err = rawError instanceof Error ? rawError : new Error(String(rawError));
					setInFlight(false);
					setError(friendlyDraftError(err.message, extensionsFrom(rawError)));
					optionsRef.current?.onError?.(err);
					reject(err);
				}
			);
		});
	}, []);

	return {generate, inFlight, error, reset};
}
